import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  archiveKnowledge,
  batchArchiveKnowledge,
  batchPublishKnowledge,
  createKnowledge,
  fetchKnowledgeDetail,
  fetchKnowledgeEntries,
  fetchKnowledgeIndexStatus,
  fetchKnowledgeOverview,
  fetchKnowledgeProducts,
  fetchKnowledgeReviewFacets,
  fetchKnowledgeReviewQueue,
  fetchKnowledgeReviewReasonCodes,
  fetchKnowledgeReviewSummary,
  publishKnowledge,
  regeneratePublicVersion,
  reindexPendingKnowledge,
  searchKnowledge,
  updateKnowledge,
} from '@/services/api/knowledge'
import type { ReviewQueueParams } from '@/services/api/knowledge'
import type {
  KnowledgeCreatePayload,
  KnowledgeListParams,
  KnowledgeReasonCodes,
  KnowledgeReviewFacets,
  KnowledgeReviewQueue,
  KnowledgeSearchPayload,
  KnowledgeUpdatePayload,
} from '@/types/knowledge'

export function useKnowledgeList(params: KnowledgeListParams, enabled = true) {
  return useQuery({
    queryKey: ['knowledge', 'list', params],
    queryFn: () => fetchKnowledgeEntries(params),
    enabled,
    staleTime: 30_000,
  })
}

export function useKnowledgeDetail(entryId: number | null) {
  return useQuery({
    queryKey: ['knowledge', 'detail', entryId],
    queryFn: () => fetchKnowledgeDetail(entryId!),
    enabled: !!entryId,
  })
}

/** 概览：规模摘要 + 筛选器选项。status 与列表同口径，避免数字与结果对不上 */
export function useKnowledgeOverview(status?: string) {
  return useQuery({
    queryKey: ['knowledge', 'overview', status ?? 'all'],
    queryFn: () => fetchKnowledgeOverview(status),
    staleTime: 30_000,
  })
}

/**
 * 分产品统计：产品维度浏览的数据源。
 *
 * 不传 status 时返回全量彩页清单（供"人工录入"的产品下拉用，与状态无关）；
 * 传了则按该状态统计（供产品面板用，必须与列表同口径）。
 */
export function useKnowledgeProducts(status?: string) {
  return useQuery({
    queryKey: ['knowledge', 'products', status ?? 'all'],
    queryFn: () => fetchKnowledgeProducts(status),
    staleTime: 30_000,
  })
}

/** 语义检索。payload 为 null 时不发请求（关键词模式下不需要） */
export function useKnowledgeSearch(payload: KnowledgeSearchPayload | null) {
  return useQuery({
    queryKey: ['knowledge', 'search', payload],
    queryFn: () => searchKnowledge(payload!),
    enabled: !!payload,
    staleTime: 30_000,
  })
}

export function useKnowledgeIndexStatus() {
  return useQuery({
    queryKey: ['knowledge', 'index-status'],
    queryFn: fetchKnowledgeIndexStatus,
    staleTime: 30_000,
  })
}

export function useCreateKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: KnowledgeCreatePayload) => createKnowledge(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

export function useUpdateKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ entryId, payload }: { entryId: number; payload: KnowledgeUpdatePayload }) =>
      updateKnowledge(entryId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

export function usePublishKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      entryId,
      reviewer,
      note,
      reasonCode,
    }: {
      entryId: number
      reviewer?: string
      note?: string
      // 结构化理由码（一次点击，可选）。它是后续打分的金标来源。
      reasonCode?: string
    }) => publishKnowledge(entryId, { reviewer, note, reasonCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

export function useArchiveKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    // note/reasonCode 都可选：不填时后端自动记一条事实流水（谁、何时、从什么状态到什么状态），
    // 与 ArchiveKnowledgeRequest 同口径 —— 前端不再比后端更严，避免"审核必须填理由"复活。
    mutationFn: ({ entryId, note, reasonCode }: { entryId: number; note?: string; reasonCode?: string }) =>
      archiveKnowledge(entryId, { note, reasonCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

export function useRegeneratePublicVersion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (entryId: number) => regeneratePublicVersion(entryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

// ── 审核工作台 ──────────────────────────────────────────────────────────

export function useKnowledgeReviewSummary(enabled = true) {
  return useQuery({
    queryKey: ['knowledge', 'review', 'summary'],
    queryFn: fetchKnowledgeReviewSummary,
    enabled,
    staleTime: 15_000,
  })
}

export function useKnowledgeReviewFacets(signal: string | undefined, enabled = true) {
  return useQuery<KnowledgeReviewFacets>({
    queryKey: ['knowledge', 'review', 'facets', signal ?? ''],
    queryFn: () => fetchKnowledgeReviewFacets(signal),
    enabled,
    staleTime: 30_000,
  })
}

export function useKnowledgeReviewQueue(params: ReviewQueueParams, enabled = true) {
  return useQuery<KnowledgeReviewQueue>({
    queryKey: ['knowledge', 'review', 'queue', params],
    queryFn: () => fetchKnowledgeReviewQueue(params),
    enabled,
    staleTime: 15_000,
  })
}

export function useBatchPublishKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      entryIds,
      note,
      reasonCode,
    }: {
      entryIds: number[]
      note?: string
      reasonCode?: string
    }) => batchPublishKnowledge(entryIds, { note, reasonCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

export function useBatchArchiveKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    // 与 useArchiveKnowledge 同口径：说明/理由码可选，不填由后端自动记事实流水
    mutationFn: ({
      entryIds,
      note,
      reasonCode,
    }: {
      entryIds: number[]
      note?: string
      reasonCode?: string
    }) => batchArchiveKnowledge(entryIds, { note, reasonCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}

/** 退回/通过的可选理由码（后端下发，前端不写死口径）。 */
export function useKnowledgeReviewReasonCodes(enabled = true) {
  return useQuery<KnowledgeReasonCodes>({
    queryKey: ['knowledge', 'review', 'reason-codes'],
    queryFn: fetchKnowledgeReviewReasonCodes,
    enabled,
    // 理由码是常量口径，没必要反复拉
    staleTime: Infinity,
  })
}

export function useReindexPendingKnowledge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (limit?: number) => reindexPendingKnowledge(limit),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['knowledge'] })
    },
  })
}
